import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, PlusCircle, ArrowRight, Smartphone, Camera, PenTool } from "lucide-react";

const KIND_LABEL = {
  entry: "Entrée", exit: "Sortie", intermediate: "Intermédiaire", control: "Contrôle",
};
const STATUS_TONE = { draft: "warning", signed: "success", archived: "neutral" };
const STATUS_LABEL = { draft: "Brouillon", signed: "Signé", archived: "Archivé" };

export default function InventoriesPage() {
  const [items, setItems] = useState([]);
  const [leases, setLeases] = useState([]);
  const [meta, setMeta] = useState({ kinds: [] });
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lease_id: "", kind: "entry", executor_name: "" });
  const navigate = useNavigate();

  const load = () => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    api.get("/inventories", { params }).then(({ data }) => setItems(data.items || []));
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line
  useEffect(() => {
    api.get("/leases").then(({ data }) => setLeases(data.items || []));
    api.get("/inventories/meta").then(({ data }) => setMeta(data));
  }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/inventories", form);
      toast.success("État des lieux créé");
      setOpen(false);
      navigate(`/app/inventories/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="animate-fade-up" data-testid="inventories-page">
      <PageHeader
        overline="Terrain"
        title="États des lieux"
        description="Créez, complétez, signez et générez des états des lieux d'entrée et de sortie — utilisable sur smartphone, tablette et ordinateur."
      />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <FeatureBox icon={Smartphone} title="Mobile-first" text="Grosses cibles tactiles, saisie une main." />
        <FeatureBox icon={Camera} title="Photos illimitées" text="Multi-import, annotation, horodatage, EXIF." />
        <FeatureBox icon={PenTool} title="Signatures + PDF" text="Signature électronique, PDF avec QR d'authenticité." />
      </section>

      <div className="flex items-center justify-between gap-3 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="inventory-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="draft">Brouillons</SelectItem>
            <SelectItem value="signed">Signés</SelectItem>
            <SelectItem value="archived">Archivés</SelectItem>
          </SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="inventory-new-btn"><PlusCircle className="mr-2 h-4 w-4" />Nouvel EDL</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Créer un état des lieux</DialogTitle>
              <DialogDescription>Rattaché à un bail existant. Les 11 pièces par défaut seront créées automatiquement.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Bail</Label>
                <Select value={form.lease_id} onValueChange={(v) => setForm({ ...form, lease_id: v })}>
                  <SelectTrigger data-testid="new-inv-lease"><SelectValue placeholder="Sélectionner un bail…" /></SelectTrigger>
                  <SelectContent>
                    {leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.lot_reference} — {(l.tenant_names || []).join(", ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type d&apos;EDL</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="new-inv-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.kinds.map((k) => <SelectItem key={k.code} value={k.code}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Intervenant</Label>
                <Input value={form.executor_name} onChange={(e) => setForm({ ...form, executor_name: e.target.value })} placeholder="Nom du gestionnaire, huissier…" data-testid="new-inv-executor" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} disabled={!form.lease_id} data-testid="new-inv-submit">Créer et ouvrir</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left">
              <th className="px-6 py-3">Bien</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Intervenant</th>
              <th className="px-6 py-3">Programmé</th>
              <th className="px-6 py-3">Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-muted-foreground">
                <ClipboardCheck className="mx-auto h-10 w-10 mb-3 opacity-60" />
                Aucun état des lieux. Cliquez sur « Nouvel EDL » pour commencer.
              </td></tr>
            )}
            {items.map((i) => (
              <tr key={i.id} data-testid={`inventory-row-${i.id}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3">
                  <div className="font-medium">{i.lot_reference || "—"}</div>
                  <div className="text-xs text-muted-foreground">{i.building_name}</div>
                </td>
                <td className="px-6 py-3">{KIND_LABEL[i.kind]}</td>
                <td className="px-6 py-3 text-xs">{i.executor_name || "—"}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{i.scheduled_at ? new Date(i.scheduled_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="px-6 py-3"><StatusBadge tone={STATUS_TONE[i.status]}>{STATUS_LABEL[i.status]}</StatusBadge></td>
                <td className="px-6 py-3 text-right">
                  <Link to={`/app/inventories/${i.id}`}>
                    <Button variant="ghost" size="sm" data-testid={`inventory-open-${i.id}`}>Ouvrir <ArrowRight className="ml-1 h-3 w-3" /></Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FeatureBox({ icon: Icon, title, text }) {
  return (
    <div className="card-elevated p-5 flex items-start gap-3">
      <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0"><Icon className="h-5 w-5 text-primary" /></div>
      <div><div className="font-heading">{title}</div><div className="text-xs text-muted-foreground mt-1">{text}</div></div>
    </div>
  );
}
